import type { z } from "zod";

import type { HotelFormValuesSchema } from "@/features/hotels/components/hotels/validation";
import type {
  FindUserTravelersOutputData,
  InitiateHotelsSearchOutputData,
  FindHotelOffersRoom,
  FindHotelOffersOutputData,
  FindHotelOffersPackageRoomOption,
} from "@/types/codegen";
import type { SortValue } from "@/types/types";

export type TRoom = {
  adults: number;
  children: number;
  childrenAges: (number | string)[];
};

export type THotelFormFieldValues = z.input<HotelFormValuesSchema>;
export type THotelFormValues = z.infer<HotelFormValuesSchema>;

export type THotelSearchResult = {
  id: number | string;
  name: string;
  address: string;
  specialDeal?: boolean;
  recommended?: boolean;
  descriptions?: {
    short: string;
    full: string;
    location: string;
    facilities: string;
    rooms: string;
    essentialInformation: string;
  };
  boardBasis?: string[];
  stars: number;
  mainImageUrl: string;
  imagesUrls: string[];
  telephone?: string;
  email?: string;
  fax?: string;
  minPrice?: {
    value: number;
    currency: string;
  };
  geolocation?: {
    latitude: number;
    longitude: number;
  };
};

export type HotelSearchTokens = InitiateHotelsSearchOutputData;

type HotelOptionalKeys =
  | "telephone"
  | "email"
  | "fax"
  | "geolocation"
  | "address"
  | "imagesUrls";

export type Hotel = Omit<THotelSearchResult, HotelOptionalKeys> &
  Partial<Pick<THotelSearchResult, HotelOptionalKeys>>;
export type THotelRoom = {
  name: string;
  board: string;
} & FindHotelOffersRoom;

export type TRoomOutput = {
  packageRoomToken: string;
  travelers: FindUserTravelersOutputData[];
};

export interface Price {
  value: number;
  currency: string;
}

export type HotelBookingRoomOption = FindHotelOffersPackageRoomOption;

export type HotelOfferRoom = FindHotelOffersRoom;

export type HotelOffer = FindHotelOffersOutputData;

export interface HotelBooking {
  packageToken: string;
  offerId: string;
  price?: Price;
  rooms: HotelBookingRoomOption[];
  availabilityToken?: string;
}

export type HotelsSortValue = SortValue<HotelsSortOptions>;

export interface IHotelsFilters {
  minPrice?: number;
  maxPrice?: number;
  priceRange: [number, number];
  boardBasis?: string[];
  starRating: number[];
}

export enum HotelsSortOptions {
  PRICE = "price",
  RATING = "stars",
  RECOMMENDED = "recommended",
  SPECIALDEAL = "specialDeal",
}

export enum TravelPurpose {
  LEISURE = "leisure",
  BUSINESS = "business",
}
